<template>
  <div class="login-container">
    <div class="login-box">
      <div class="logo-container">
        <BrandLogo :height="48" />
        <p class="subtitle">{{ $t('auth.login.subtitle') }}</p>
      </div>
      <form ref="loginFormRef" class="form" @submit.prevent="handleSubmit">
        <div class="input-group">
          <div class="input-field">
            <label for="email-address" class="input-label">{{ $t('auth.login.emailLabel') }} <span class="required">*</span></label>
            <div class="input-wrapper">
              <span class="input-icon">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                  <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                </svg>
              </span>
              <input id="email-address" name="email" type="email" autocomplete="email" required v-model="email"
                class="input" :placeholder="$t('auth.login.emailPlaceholder')" />
            </div>
          </div>
          <div class="input-field">
            <label for="password" class="input-label">{{ $t('auth.login.passwordLabel') }} <span class="required">*</span></label>
            <div class="input-wrapper">
              <span class="input-icon">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd"
                    d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                    clip-rule="evenodd" />
                </svg>
              </span>
              <input id="password" name="password" type="password" autocomplete="current-password" required
                v-model="password" class="input" :placeholder="$t('auth.login.passwordPlaceholder')" />
            </div>
          </div>
        </div>

        <div>
          <button type="submit" :disabled="loading" class="submit-button">
            <span class="button-icon">
              <svg v-if="!loading" class="icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"
                fill="currentColor" aria-hidden="true">
                <path fill-rule="evenodd"
                  d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                  clip-rule="evenodd" />
              </svg>
              <AppPageLoader v-else bare size="sm" />
            </span>
            {{ loading ? $t('auth.login.signingIn') : $t('auth.login.signIn') }}
          </button>
        </div>

        <div v-if="error" class="error-message">
          {{ error }}
        </div>

        <div class="mt-4 text-center register-link">
          <p class="text-sm text-gray-600">
            <router-link to="/forgot-password" class="register-link-button">
              {{ $t('auth.forgotPassword.link') }}
            </router-link>
          </p>
          <p class="text-sm text-gray-600 mt-2">
            {{ $t('auth.notRegistered') }}
            <router-link to="/admin-register" class="register-link-button">
              {{ $t('auth.registerAsAdmin') }}
            </router-link>
          </p>
          <p class="text-sm text-gray-600 mt-2">
            <router-link to="/privacy-policy" class="register-link-button">
              {{ $t('auth.privacyPolicy') }}
            </router-link>
          </p>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@/stores/auth'
import { getApiErrorMessage } from '@/utils/apiErrors'
import { scrollToFirstNativeInvalid, stopGuidedFormErrorNavigation } from '@/utils/scrollToFirstFormError'
import BrandLogo from '@/components/BrandLogo.vue'

const loginFormRef = ref<HTMLFormElement | null>(null)

const auth = useAuthStore()
const { t } = useI18n()
const email = ref('')
const password = ref('')
const loading = ref(false)
const error = ref('')

const handleSubmit = async () => {
  if (!loginFormRef.value?.checkValidity()) {
    await scrollToFirstNativeInvalid(loginFormRef.value)
    return
  }

  stopGuidedFormErrorNavigation()

  loading.value = true
  error.value = ''

  try {
    await auth.adminLogin(email.value, password.value)
  } catch (err: any) {
    error.value = getApiErrorMessage(err, t('auth.login.errorGeneric'))
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
.login-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #374151 0%, #1f2937 100%);
  padding: 1rem;
  background-size: cover;
  position: relative;
}

.login-container::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url('data:image/svg+xml;charset=utf8,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 320"%3E%3Cpath fill="%23D8670C" fill-opacity="0.1" d="M0,64L48,96C96,128,192,192,288,192C384,192,480,128,576,122.7C672,117,768,171,864,197.3C960,224,1056,224,1152,197.3C1248,171,1344,117,1392,90.7L1440,64L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"%3E%3C/path%3E%3C/svg%3E');
  background-size: cover;
  background-position: center;
  z-index: 0;
  opacity: 0.8;
}

.login-box {
  width: 100%;
  max-width: 420px;
  margin: 0 auto;
  background: rgba(255, 255, 255, 0.85);
  padding: 3rem 2.5rem;
  border-radius: 1.25rem;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.3), 0 10px 10px -5px rgba(0, 0, 0, 0.1);
  position: relative;
  z-index: 1;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.7) 100%);
  backdrop-filter: blur(15px);
  transform: translateY(0);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  overflow: hidden;
}

.login-box::before {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0) 80%);
  transform: rotate(30deg);
  z-index: -1;
}

.login-box:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 30px -5px rgba(0, 0, 0, 0.4), 0 10px 15px -5px rgba(0, 0, 0, 0.2);
}

@media (max-width: 640px) {
  .login-box {
    padding: 2rem 1.5rem;
    margin: 1rem;
  }

  .subtitle {
    font-size: 0.875rem;
  }

  .form {
    margin-top: 1.5rem;
  }

  .input {
    padding: 0.75rem;
  }

  .submit-button {
    padding: 0.875rem;
  }
}

.logo-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 2rem;
  gap: 0.75rem;
}

.subtitle {
  margin-top: 0;
  text-align: center;
  font-size: 1rem;
  color: #4b5563;
  animation: fadeIn 0.7s ease-out;
}

.form {
  margin-top: 2rem;
  animation: fadeIn 0.7s ease-out;
}

.input-group {
  margin-bottom: 1.75rem;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.input-field {
  margin-bottom: 0;
}

.input-label {
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: #4b5563;
  margin-bottom: 0.5rem;
}

.input-wrapper {
  position: relative;
  border-radius: 0.5rem;
  overflow: hidden;
  background-color: rgba(249, 250, 251, 0.7);
  border: 1px solid rgba(229, 231, 235, 0.8);
  transition: all 0.2s ease;
  backdrop-filter: blur(5px);
}

.input-wrapper:focus-within {
  border-color: #D8670C;
  box-shadow: 0 0 0 3px rgba(216, 103, 12, 0.1);
  background-color: rgba(255, 255, 255, 0.9);
}

.input-icon {
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-65%);
  width: 1.25rem;
  height: 1.25rem;
  color: #9ca3af;
}

.input {
  display: block;
  width: 100%;
  padding: 0.875rem 1rem 0.875rem 3rem;
  background-color: transparent;
  color: #1a1a1a;
  font-size: 0.95rem;
  border: none;
  transition: all 0.2s ease;
}

.input::placeholder {
  color: #9ca3af;
}

.input:focus {
  outline: none;
}

.submit-button {
  position: relative;
  width: 100%;
  display: flex;
  justify-content: center;
  padding: 1rem 1.25rem;
  border: none;
  border-radius: 0.5rem;
  font-size: 1rem;
  font-weight: 500;
  color: white;
  background: linear-gradient(135deg, #D8670C 0%, #c05a0a 100%);
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 6px -1px rgba(216, 103, 12, 0.3), 0 2px 4px -1px rgba(216, 103, 12, 0.15);
  overflow: hidden;
}

.submit-button:after {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(to right,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.2) 50%,
      rgba(255, 255, 255, 0) 100%);
  transition: all 0.6s;
}

.submit-button:hover {
  background: linear-gradient(135deg, #c05a0a 0%, #a24a08 100%);
  transform: translateY(-2px);
  box-shadow: 0 6px 10px -1px rgba(216, 103, 12, 0.4), 0 4px 6px -1px rgba(216, 103, 12, 0.2);
}

.submit-button:hover:after {
  left: 100%;
}

.submit-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.button-icon {
  position: absolute;
  left: 1rem;
  display: flex;
  align-items: center;
}

.icon {
  width: 1.25rem;
  height: 1.25rem;
}

.error-message {
  margin-top: 1rem;
  padding: 0.75rem;
  background-color: rgba(254, 226, 226, 0.7);
  border: 1px solid rgba(252, 165, 165, 0.8);
  border-radius: 0.5rem;
  color: #b91c1c;
  font-size: 0.875rem;
  animation: shake 0.5s cubic-bezier(.36, .07, .19, .97) both;
  backdrop-filter: blur(5px);
}

.register-link {
  margin-top: 1.5rem;
  text-align: center;
}

.register-link a,
.register-link-button {
  color: #D8670C;
  font-weight: 500;
  text-decoration: underline;
  transition: color 0.2s ease;
}

.register-link a:hover,
.register-link-button:hover {
  color: #c05a0a;
  text-decoration: underline;
}

@keyframes shake {

  10%,
  90% {
    transform: translateX(-1px);
  }

  20%,
  80% {
    transform: translateX(2px);
  }

  30%,
  50%,
  70% {
    transform: translateX(-4px);
  }

  40%,
  60% {
    transform: translateX(4px);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>